/**
 *
 *  Become a Partner page
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 23/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React from "react";
import Slider from "react-slick";
import { Title, Meta } from "react-meta-elements";

import { useAppState } from "../../state";

// Components
import Header from "./Header/Header";
import Footer from "../Footer/Footer";
import Button from "../Button/Button";
import ScrollToTopOnMount from "../ScrollToTopOnMount/ScrollToTopOnMount";
import { ProgressiveImage } from "../ProgressiveImage/ProgressiveImage";

// Hooks
import useTheme from "../ThemeProvider/useTheme/useTheme";

// Icons
import ArrowLeftIcon from "../../icons/ArrowLeftIcon";
import ArrowRightIcon from "../../icons/ArrowRightIcon";

// Utils
import { clientWidth } from "../../utils";

// Images
import Arrow from "./Arrow";

import becomeAPartnerHero from "../../images/become-a-partner-hero.jpg";
import becomeAPartnerHero2x from "../../images/become-a-partner-hero@2x.jpg";
import becomeAPartnerHeroMobile from "../../images/become-a-partner-hero-mobile.jpg";
import becomeAPartnerHeroMobile2x from "../../images/become-a-partner-hero-mobile@2x.jpg";

import becomeAPartnerHeroBottom from "../../images/become-a-partner-hero-bottom.jpg";
import becomeAPartnerHeroBottom2x from "../../images/become-a-partner-hero-bottom@2x.jpg";
import becomeAPartnerHeroBottomMobile from "../../images/become-a-partner-hero-bottom-mobile.jpg";
import becomeAPartnerHeroBottomMobile2x from "../../images/become-a-partner-hero-bottom-mobile@2x.jpg";

import becomeAPartnerTrainers from "../../images/become-a-partner-trainers.jpg";
import becomeAPartnerTrainers2x from "../../images/become-a-partner-trainers@2x.jpg";
import becomeAPartnerTrainersMobile from "../../images/become-a-partner-trainers-mobile.jpg";
import becomeAPartnerTrainersMobile2x from "../../images/become-a-partner-trainers-mobile@2x.jpg";

import TruBeLogo from "./TruBeLogo";

export default function BecomePartnerPage() {
  const { theme } = useTheme();
  const { user } = useAppState();

  const heroRef = React.useRef(null);

  return (
    <>
      <ScrollToTopOnMount />
      <Title>TruBe: Welcome to Team TruBe</Title>
      <Meta
        name="description"
        content="Join TruBe as a trainer and thrive with our innovative platform, comprehensive certification program, and exciting opportunities in fitness and wellbeing."
      />
      <Header
        behaviour="none"
        isLoggedIn={!!(user && user.id)}
        showRegionSelect
        // region="US"
        heroRef={heroRef}
      />

      <div
        ref={heroRef}
        className="tbk-relative tbk-flex tbk-h-140 tbk-pt-7 md:tbk-h-auto"
      >
        <div className="tbk-absolute tbk-bottom-0 tbk-left-0 tbk-right-0 tbk-top-7 tbk-flex tbk-w-full tbk-items-end tbk-overflow-hidden tbk-bg-grey-light">
          <picture className="tbk-h-full tbk-grow">
            <source
              media="(min-width: 768px)"
              srcSet={`${becomeAPartnerHero} 1x, ${becomeAPartnerHero2x} 2x,`}
            />
            <source
              srcSet={`${becomeAPartnerHeroMobile} 1x, ${becomeAPartnerHeroMobile2x} 2x,`}
            />

            <img
              className="tbk-min-h-full tbk-w-full tbk-object-cover md:tbk-h-full"
              src={becomeAPartnerHero}
              alt="Get more fitness clients"
            />
          </picture>
        </div>
        <div className="tbk-container tbk-z-1 tbk-mx-auto tbk-px-2 lg:tbk-px-8">
          <div className="tbk-relative tbk-h-full tbk-pt-50 md:tbk-grid md:tbk-grid-cols-2 md:tbk-items-end lg:tbk-pb-8 lg:tbk-pb-8 lg:tbk-pt-4 lg:tbk-pt-64">
            <div className="">
              <div className="tbk-text-extra-bold lg:tbk-text-accident !tbk-leading-none tbk-text-basic-white lg:tbk-whitespace-nowrap">
                We
                <br />
                Need
                <br />
                <span className="tbk-text-coral">You.</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-bg-coral tbk-pb-7 tbk-pt-7 lg:tbk-pb-8">
        <div className="tbk-container tbk-px-3 tbk-text-center lg:tbk-px-8 lg:tbk-pb-5">
          <p className="tbk-text-title-bold lg:tbk-text-epic-medium tbk-mb-7 !tbk-font-semibold !tbk-tracking-tight tbk-text-basic-white lg:tbk-mb-7">
            Coaches are the ambassadors of the TruBe Brand. They lead the charge
            in on-demand fitness training and wellness programming. As a TruBe
            coach you bring accessibility to personal training by offering
            in-person, one-on-one sessions with our Members.
          </p>
          <a
            href="https://share.hsforms.com/1xmt3pDR5S6yahr4qCvmvdAnv2ly"
            target="_blank"
            rel="noreferrer"
          >
            <Button
              // color="basic"
              variant="secondary"
              className="tbk-inline-block lg:tbk-w-66"
              endIcon={<ArrowRightIcon />}
            >
              <span className="tbk-whitespace-nowrap tbk-pl-5 tbk-pr-5">
                Join the Team
              </span>
            </Button>
          </a>
        </div>
      </div>

      <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-bg-basic-white tbk-pb-7 tbk-pt-7 lg:tbk-pb-8">
        <div className="tbk-container tbk-px-2 lg:tbk-px-8 lg:tbk-pb-5">
          <p className="tbk-text-title-bold lg:tbk-text-epic-medium tbk-mb-7 tbk-text-center !tbk-font-semibold !tbk-tracking-tighter tbk-text-grey-main lg:tbk-mb-7">
            TruBe is curating a Community of leading fitness and wellness
            coaches. Every coach uplifts the other by contributing their
            knowledge and expertise to the TruBe ecosystem to learn and grow
            from each other.
            <br />
            <br />
            These are the coaching qualifications you need to join us:
          </p>
          <div className="md:tbk-grid md:tbk-grid-cols-3 md:tbk-items-start md:tbk-gap-2">
            <div className="tbk-mb-4 tbk-flex lg:tbk-mb-0 lg:tbk-p-2">
              <div className="tbk-ml-0.5 tbk-mr-1.5 tbk-mt-1 tbk-h-1.5 tbk-w-1.5 tbk-shrink-0 tbk-rounded-full tbk-border tbk-border-solid tbk-border-coral tbk-bg-coral" />
              <div className="tbk-text-title-medium tbk-text-grey-main">
                Professional, knowledgable and passionate about fitness and
                wellness.
              </div>
            </div>

            <div className="tbk-mb-4 tbk-flex lg:tbk-mb-0 lg:tbk-p-2">
              <div className="tbk-ml-0.5 tbk-mr-1.5 tbk-mt-1 tbk-h-1.5 tbk-w-1.5 tbk-shrink-0 tbk-rounded-full tbk-border tbk-border-solid tbk-border-coral tbk-bg-coral" />
              <div className="tbk-text-title-medium tbk-text-grey-main">
                Liability insurance minimum requirement.
              </div>
            </div>

            <div className="tbk-mb-4 tbk-flex lg:tbk-mb-0 lg:tbk-p-2">
              <div className="tbk-ml-0.5 tbk-mr-1.5 tbk-mt-1 tbk-h-1.5 tbk-w-1.5 tbk-shrink-0 tbk-rounded-full tbk-border tbk-border-solid tbk-border-coral tbk-bg-coral" />
              <div className="tbk-text-title-medium tbk-text-grey-main">
                Minimum 1 year training experience.
              </div>
            </div>

            <div className="tbk-mb-4 tbk-flex lg:tbk-mb-0 lg:tbk-p-2">
              <div className="tbk-ml-0.5 tbk-mr-1.5 tbk-mt-1 tbk-h-1.5 tbk-w-1.5 tbk-shrink-0 tbk-rounded-full tbk-border tbk-border-solid tbk-border-coral tbk-bg-coral" />
              <div className="tbk-text-title-medium tbk-text-grey-main">
                Employment eligibility to work in the United States.
              </div>
            </div>

            <div className="tbk-mb-4 tbk-flex lg:tbk-mb-0 lg:tbk-p-2">
              <div className="tbk-ml-0.5 tbk-mr-1.5 tbk-mt-1 tbk-h-1.5 tbk-w-1.5 tbk-shrink-0 tbk-rounded-full tbk-border tbk-border-solid tbk-border-coral tbk-bg-coral" />
              <div className="tbk-text-title-medium tbk-text-grey-main">
                Desire to feel good, look good, and do good.
              </div>
            </div>

            <div className="tbk-mb-4 tbk-flex lg:tbk-mb-0 lg:tbk-p-2">
              <div className="tbk-ml-0.5 tbk-mr-1.5 tbk-mt-1 tbk-h-1.5 tbk-w-1.5 tbk-shrink-0 tbk-rounded-full tbk-border tbk-border-solid tbk-border-coral tbk-bg-coral" />
              <div className="tbk-text-title-medium tbk-text-grey-main">
                iOS or Android device.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        // className="tbk-flex tbk-h-[403px] tbk-flex-col tbk-items-center tbk-justify-center tbk-bg-trainers-hero-bottom-mobile tbk-bg-cover tbk-bg-center tbk-bg-no-repeat lg:tbk-h-170 lg:tbk-bg-trainers-hero-bottom"
        className="tbk-relative tbk-h-[403px] lg:tbk-h-170"
      >
        <ProgressiveImage
          theme={theme}
          className="tbk-absolute tbk-inset-0 tbk-z-0"
          loaderClassName="tbk-rounded-none"
          imgStyle={{ objectFit: "cover", width: "100%", height: "100%" }}
          width="100%"
          height="100%"
          alt="Trainers Hero Bottom"
          defaultBreakpoint="xs"
          imgConfig={[
            {
              breakpoint: "xs",
              url: becomeAPartnerTrainersMobile,
              url2x: becomeAPartnerTrainersMobile2x,
            },
            {
              breakpoint: "sm",
              url: becomeAPartnerTrainers,
              url2x: becomeAPartnerTrainers2x,
            },
          ]}
        />
      </div>

      <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-bg-basic-white tbk-pb-7 tbk-pt-7 lg:tbk-pb-8 lg:tbk-pt-28">
        <div className="tbk-container tbk-px-3 lg:tbk-px-2 lg:tbk-pb-5">
          <div className="md:tbk-grid md:tbk-grid-cols-3 md:tbk-items-start md:tbk-gap-7">
            <div className="tbk-mb-7 lg:tbk-mb-2">
              <h3 className="tbk-text-h2-black lg:tbk-text-epic-medium tbk-mb-3 !tbk-leading-small tbk-text-coral lg:!tbk-leading-none">
                Be Part of a Community
              </h3>
              <p className="tbk-text-h3-subtitle lg:tbk-text-title-bold tbk-text-grey-main">
                TruBe is bringing forward a community network of leading
                trainers and coaches in fitness and wellness. Each coach uplifts
                the other by providing an ecosystem to learn and grow from each
                other.
              </p>
            </div>

            <div className="tbk-mb-7 lg:tbk-mb-2">
              <h3 className="tbk-text-h2-black lg:tbk-text-epic-medium tbk-mb-3 !tbk-leading-small tbk-text-coral lg:!tbk-leading-none">
                Enhance Your Program&nbsp;&&nbsp;Tools
              </h3>
              <p className="tbk-text-h3-subtitle lg:tbk-text-title-bold tbk-text-grey-main">
                Take your coaching program to the next level. Utilize the power
                of TruBe to better yourself and the experience you give to
                Members.
              </p>
            </div>

            <div className="lg:tbk-mb-2">
              <h3 className="tbk-text-h2-black lg:tbk-text-epic-medium tbk-mb-3 !tbk-leading-small tbk-text-coral lg:!tbk-leading-none">
                Grow Your
                <br />
                Business
              </h3>
              <p className="tbk-text-h3-subtitle lg:tbk-text-title-bold tbk-text-grey-main">
                TruBe is building the structure to grow your business, build
                your client base and increase your income. Leverage the TruBe
                ecosystem to give you the shortcut to achieving your career
                goals and building within a company that empowers you.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-bg-coral tbk-pb-7 tbk-pt-8 lg:tbk-pb-28">
        <div className="tbk-container tbk-px-3 lg:tbk-px-2">
          <h2 className="tbk-text-h2-black lg:tbk-text-epic-bold tbk-mb-7 tbk-text-center tbk-text-basic-white lg:tbk-mb-8">
            Our Simple
            <br className="lg:tbk-hidden" /> Onboarding Process
          </h2>
          <div className="lg:tbk-mb-1 lg:tbk-grid lg:tbk-grid-cols-5 lg:tbk-items-start">
            <div className="tbk-mx-auto tbk-flex tbk-h-54 tbk-w-54 tbk-flex-col tbk-items-center lg:tbk-h-44 lg:tbk-w-44 lg:tbk-flex-row lg:tbk-items-start lg:tbk-justify-between xl:tbk-h-54 xl:tbk-w-54">
              <div className="tbk-h-full tbk-w-full tbk-flex-col tbk-rounded-full tbk-border-[3px] tbk-border-solid tbk-border-basic-white tbk-px-5 tbk-pb-7 tbk-pt-3 tbk-text-center md:tbk-px-2 lg:tbk-mb-0 lg:tbk-pb-2 lg:tbk-pt-2 xl:tbk-px-5">
                <div className="tbk-text-cta tbk-mb-2 tbk-inline-block tbk-shrink-0 tbk-whitespace-nowrap tbk-px-1.5 tbk-py-0.5 tbk-py-0.5 tbk-uppercase tbk-text-grey-light lg:tbk-mb-1 lg:tbk-mb-2 xl:tbk-mb-2">
                  Step 1
                </div>
                <div className="tbk-text-title-bold tbk-mb-3 tbk-text-center tbk-text-basic-white lg:tbk-mb-0 xl:tbk--ml-1">
                  Complete Application
                </div>
              </div>
              <Arrow className="tbk-mx-0.5 tbk-mb-3 tbk-mt-1.5 tbk-rotate-90 lg:tbk-mt-8 lg:tbk-rotate-0 xl:tbk-mx-1 xl:tbk-mt-24" />
            </div>

            <div className="tbk-mx-auto tbk-mt-5 tbk-flex tbk-h-54 tbk-w-54 tbk-flex-col tbk-items-center lg:tbk-mt-0 lg:tbk-h-44 lg:tbk-w-44 lg:tbk-flex-row lg:tbk-items-start lg:tbk-justify-between xl:tbk-h-54 xl:tbk-w-54">
              <div className="tbk-h-full tbk-w-full tbk-flex-col tbk-rounded-full tbk-border-[3px] tbk-border-solid tbk-border-basic-white tbk-px-5 tbk-pb-7 tbk-pt-3 tbk-text-center md:tbk-px-2 lg:tbk-mb-0 lg:tbk-pb-2 lg:tbk-pt-2 xl:tbk-px-5">
                <div className="tbk-text-cta tbk-mb-2 tbk-inline-block tbk-shrink-0 tbk-whitespace-nowrap tbk-px-1.5 tbk-py-0.5 tbk-py-0.5 tbk-uppercase tbk-text-grey-light lg:tbk-mb-1 lg:tbk-mb-2 xl:tbk-mb-2">
                  Step 2
                </div>
                <div className="tbk-text-title-bold tbk-mb-3 tbk-text-center tbk-text-basic-white lg:tbk-mb-0">
                  Submit
                  <br className="lg:tbk-hidden" /> Documents
                </div>
              </div>
              <Arrow className="tbk-mx-0.5 tbk-mb-3 tbk-mt-1.5 tbk-rotate-90 lg:tbk-mt-8 lg:tbk-rotate-0 xl:tbk-mx-1 xl:tbk-mt-24" />
            </div>

            <div className="tbk-mx-auto tbk-mt-5 tbk-flex tbk-h-54 tbk-w-54 tbk-flex-col tbk-items-center lg:tbk-mt-0 lg:tbk-h-44 lg:tbk-w-44 lg:tbk-flex-row lg:tbk-items-start lg:tbk-justify-between xl:tbk-h-54 xl:tbk-w-54">
              <div className="tbk-h-full tbk-w-full tbk-flex-col tbk-rounded-full tbk-border-[3px] tbk-border-solid tbk-border-basic-white tbk-px-6 tbk-pb-8 tbk-pt-3 tbk-text-center md:tbk-px-4 lg:tbk-mb-0 lg:tbk-pb-2 lg:tbk-pt-2 xl:tbk-px-6">
                <div className="tbk-text-cta tbk-mb-4 tbk-inline-block tbk-shrink-0 tbk-whitespace-nowrap tbk-px-1.5 tbk-py-0.5 tbk-py-0.5 tbk-uppercase tbk-text-grey-light lg:tbk-mb-3 xl:tbk-mb-4">
                  Step 3
                </div>
                <div className="tbk-text-title-bold tbk--ml-1 tbk-mb-3 tbk-text-basic-white lg:tbk-mb-0 lg:tbk-ml-0 xl:tbk--ml-1">
                  Interview
                </div>
              </div>
              <Arrow className="tbk-mx-0.5 tbk-mb-3 tbk-mt-1.5 tbk-rotate-90 lg:tbk-mt-8 lg:tbk-rotate-0 xl:tbk-mx-1 xl:tbk-mt-24" />
            </div>

            <div className="tbk-mx-auto tbk-mt-5 tbk-flex tbk-h-54 tbk-w-54 tbk-flex-col tbk-items-center lg:tbk-mt-0 lg:tbk-h-44 lg:tbk-w-44 lg:tbk-flex-row lg:tbk-items-start lg:tbk-justify-between xl:tbk-h-54 xl:tbk-w-54">
              <div className="tbk-h-full tbk-w-full tbk-flex-col tbk-rounded-full tbk-border-[3px] tbk-border-solid tbk-border-basic-white tbk-px-4 tbk-py-2 tbk-text-center md:tbk-px-2 xl:tbk-px-4">
                <div className="tbk-text-cta tbk-mb-2 tbk-inline-block tbk-shrink-0 tbk-whitespace-nowrap tbk-px-1.5 tbk-py-0.5 tbk-py-0.5 tbk-uppercase tbk-text-grey-light lg:tbk-mb-0 xl:tbk-mb-0">
                  Step 4
                </div>
                <div className="tbk-text-title-bold tbk--mt-1 tbk-mb-3 !tbk-tracking-tighter tbk-text-basic-white lg:tbk-mb-0 lg:tbk-mt-0">
                  Brand
                  <br className="lg:tbk-hidden" /> Ambassador
                  <br className="lg:tbk-hidden" /> Workshop
                </div>
              </div>
              <Arrow className="tbk-mx-0.5 tbk-mb-3 tbk-mt-1.5 tbk-rotate-90 lg:tbk-mt-8 lg:tbk-rotate-0 xl:tbk-mx-1 xl:tbk-mt-24" />
            </div>

            <div className="tbk-mx-auto tbk-mt-5 tbk-flex tbk-h-54 tbk-w-54 tbk-flex-col tbk-items-center lg:tbk-mt-0 lg:tbk-h-44 lg:tbk-w-44 lg:tbk-flex-row lg:tbk-items-start lg:tbk-justify-between xl:tbk-h-54 xl:tbk-w-54">
              <div className="tbk-h-full tbk-w-full tbk-flex-col tbk-rounded-full tbk-border-[3px] tbk-border-solid tbk-border-basic-white tbk-px-6 tbk-pb-8 tbk-pt-3 tbk-text-center md:tbk-px-2 lg:tbk-mb-0 lg:tbk-pb-2 lg:tbk-pt-2 xl:tbk-px-6">
                <div className="tbk-text-cta tbk-mb-4 tbk-inline-block tbk-shrink-0 tbk-whitespace-nowrap tbk-px-1.5 tbk-py-0.5 tbk-py-0.5 tbk-uppercase tbk-text-grey-light lg:tbk-mb-1 lg:tbk-mb-2 xl:tbk-mb-4">
                  Step 5
                </div>
                <div className="tbk-text-title-bold tbk--ml-3 tbk-mb-3 tbk-whitespace-nowrap tbk-text-basic-white lg:tbk-mb-0">
                  Let’s Do This!
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-bg-black-grey tbk-pb-8 tbk-pt-5 lg:tbk-pb-30 lg:tbk-pt-8">
        <div className="tbk-container tbk-px-3 lg:tbk-px-8">
          <p className="tbk-text-title-bold lg:tbk-text-epic-medium tbk-mb-7 tbk-mt-8 tbk-text-center !tbk-font-semibold !tbk-tracking-tighter tbk-text-basic-white lg:tbk-mt-0">
            TruBe coaches are the backbone of the Member experience, and our
            goal is to empower you with the tools and training you need to make
            the most of this partnership - for our Members and for your
            business.
          </p>
          <div className="tbk-mb-5 tbk-text-center lg:tbk-mb-0">
            <TruBeLogo
              width={110}
              height={33}
              className="tbk-inline-block tbk-text-basic-white lg:tbk-hidden"
            />
            <TruBeLogo className="tbk-inline-block tbk-hidden tbk-text-basic-white lg:tbk-inline-block" />
          </div>
        </div>
      </div>

      <div ref={heroRef} className="tbk-relative tbk-bg-[#EDEBE6] ">
        <div className="tbk-bottom-0 tbk-left-0 tbk-right-0 tbk-top-0 tbk-flex tbk-w-full tbk-items-end tbk-overflow-hidden tbk-bg-grey-light lg:tbk-absolute">
          <ProgressiveImage
            theme={theme}
            className="tbk-grow md:tbk-h-full"
            imgStyle={{ objectFit: "cover", width: "100%", height: "100%" }}
            width="100%"
            height="100%"
            alt="Best Personal Trainer Company"
            defaultBreakpoint="xs"
            imgConfig={[
              {
                breakpoint: "xs",
                url: becomeAPartnerHeroBottomMobile,
                url2x: becomeAPartnerHeroBottomMobile2x,
              },
              {
                breakpoint: "md",
                url: becomeAPartnerHeroBottom,
                url2x: becomeAPartnerHeroBottom2x,
              },
            ]}
          />
        </div>

        <div className="tbk-container tbk-z-1 tbk-mx-auto tbk-px-2 lg:tbk-bg-basic-white lg:tbk-px-8">
          <div className="tbk-relative tbk-h-full tbk-pb-5 tbk-pt-4 md:tbk-grid md:tbk-grid-cols-2 md:tbk-items-end lg:tbk-pb-52 lg:tbk-pb-8 lg:tbk-pt-8">
            <div className="tbk-text-center lg:tbk-text-left">
              <div className="tbk-text-h2-black lg:tbk-text-epic-bold tbk-mb-3 !tbk-leading-tight tbk-text-grey-main lg:tbk-mb-5 lg:tbk-whitespace-nowrap">
                Take Your
                <br />
                Coaching <br className="tbk-hidden lg:tbk-block" />
                to the
                <br />
                Next Level.
                <br />
              </div>
              <a
                href="https://share.hsforms.com/1xmt3pDR5S6yahr4qCvmvdAnv2ly"
                target="_blank"
                rel="noreferrer"
              >
                <Button
                  color="coral"
                  // variant="secondary"
                  className="tbk-mx-auto tbk-mb-2 tbk-inline-block lg:tbk-ml-0 lg:tbk-mr-0 lg:tbk-w-66"
                  endIcon={<ArrowRightIcon />}
                >
                  <span className="tbk-whitespace-nowrap tbk-pl-5 tbk-pr-5">
                    Join the Team
                  </span>
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}
